import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const villeneuveLoubetEn: ChauffeurPage = {
  slug: "villeneuve-loubet",
  title: "VTC & private chauffeur in Villeneuve-Loubet",
  subtitle:
    "Private chauffeur in Villeneuve-Loubet: Nice airport transfers, Marina Baie des Anges, the beaches and the old village.",
  keywords: [
    "vtc villeneuve-loubet",
    "private chauffeur villeneuve-loubet",
    "marina baie des anges chauffeur",
  ],
  hero: {
    title: "VTC & private chauffeur in Villeneuve-Loubet",
    subtitle:
      "Nice airport transfers, Marina Baie des Anges, the beaches and Escoffier's old village — available 24/7.",
    keywords: [
      "VTC Villeneuve-Loubet",
      "Marina Baie des Anges chauffeur",
      "Nice airport transfer",
    ],
    imageSrc: heroImage.src,
    imageAlt:
      "VTC & private chauffeur in Villeneuve-Loubet and Marina Baie des Anges",
    badges: [
      "Marina Baie des Anges",
      "Flight tracking",
      "Bilingual chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Villeneuve-Loubet",
    items: [
      {
        title: "Marina Baie des Anges",
        description:
          "A marina and arc-shaped buildings facing the sea: drop-off at the foot of the residences and the harbour, with no search for a space.",
      },
      {
        title: "Old village & Escoffier",
        description:
          "Hilltop village, a castle keep and the Escoffier Museum of Culinary Art: drop-off as close as possible to the lanes.",
      },
      {
        title: "Nice airport in ~15 min",
        description:
          "A direct link via the A8 or the seafront, with flight tracking and a personal welcome.",
      },
      {
        title: "Local roots",
        description:
          "Based on the French Riviera, rated 5.0/5 from 38 Google reviews, with bilingual FR/EN chauffeurs.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Villeneuve-Loubet",
    services: [
      {
        title: "Nice airport transfers",
        text: "Villeneuve-Loubet ⇄ Nice Côte d'Azur airport in about 15 minutes, with flight tracking and a terminal welcome.",
      },
      {
        title: "Marina Baie des Anges",
        text: "Service to the marina, the arc-shaped residences and the seafront restaurants.",
      },
      {
        title: "Old village & Escoffier Museum",
        text: "Drop-off at the foot of the hilltop village, visit to the castle and the Escoffier Museum of Culinary Art.",
      },
      {
        title: "Beaches & Vaugrenier park",
        text: "Family days between the Villeneuve-Loubet beaches and the Vaugrenier country park.",
      },
      {
        title: "Hourly hire",
        text: "By the hour or by the day, a dedicated chauffeur for your trips along the Riviera.",
      },
    ],
  },
  locations: {
    heading: "Areas & pick-up points",
    items: [
      {
        name: "Marina Baie des Anges",
        description: "Marina and arc-shaped buildings designed by Minangoy.",
      },
      {
        name: "Villeneuve-Loubet-Plage",
        description: "Seafront, beaches and promenade.",
      },
      {
        name: "Old village",
        description:
          "Hilltop village with a medieval castle and pentagonal keep.",
      },
      {
        name: "Escoffier Museum of Culinary Art",
        description:
          "Auguste Escoffier's birthplace, in the heart of the old village.",
      },
      {
        name: "Vaugrenier park",
        description: "Large wooded country park near the shore.",
      },
      {
        name: "Nice airport",
        description: "~15 min via the A8 or the seafront.",
      },
    ],
  },
  generic: {
    heading: "Villeneuve-Loubet, from the old village to Marina Baie des Anges",
    subheading:
      "Between beaches, marina and Escoffier's hilltop village, one chauffeur for both Villeneuve-Loubets.",
    paragraphs: [
      "Villeneuve-Loubet stretches between sea and hills: on the coast, Marina Baie des Anges unfolds its arc-shaped buildings and its marina, lined with beaches and restaurants; inland, the hilltop old village watches over from its medieval castle with a pentagonal keep.",
      "It was here, in the old village, that Auguste Escoffier was born: the Escoffier Museum of Culinary Art pays him tribute. The chauffeur drops you as close as possible to the lanes, then collects you at the foot of the village to head for the beach or Vaugrenier park.",
      "About 15 minutes from Nice airport via the A8 or the seafront, Villeneuve-Loubet is ideal both for a quick transfer and for a family day — the Marineland area and the wide neighbouring beaches are minutes away.",
    ],
    bullets: [
      "Marina harbour & beaches",
      "Old village & Escoffier Museum",
      "Nice airport ~15 min",
    ],
  },
  process: {
    heading: "Book a chauffeur in Villeneuve-Loubet",
    steps: [
      "Share your route or your addresses (airport, marina, old village, beaches).",
      "We confirm the right vehicle and the fixed fare or hourly hire.",
      "Your chauffeur tracks your flight and greets you on time.",
      "Drop-off at the harbour, the foot of the village or the seafront, pick-up on request.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Villeneuve-Loubet"),
    {
      q: "How long is the drive to Nice airport from Villeneuve-Loubet?",
      a: "Allow about 15 minutes via the A8 or the seafront outside peak hours. We track your flight to adjust the pick-up time, both ways.",
    },
    {
      q: "Do you serve Marina Baie des Anges and its harbour?",
      a: "Yes, we drop off and collect at the foot of the arc-shaped buildings, at the marina and along the Villeneuve-Loubet beaches, with no need to look for a space.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible in high season and on busy weekends; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to an international clientele.",
    },
    {
      q: "Which vehicles do you offer in Villeneuve-Loubet?",
      a: "Mercedes E-Class and S-Class sedans, a 7-seat van, SUV and VIP minibus, depending on the number of passengers and the occasion.",
    },
    {
      q: "Can you reach the old village and the Escoffier Museum by car?",
      a: "Yes. The chauffeur drops you as close as possible to the hilltop old village; the lanes around the castle and the Escoffier Museum of Culinary Art continue on foot, and he collects you at the same spot.",
    },
  ],
  relatedLinks: [
    {
      label: "Nice Airport transfers",
      href: "/en/chauffeur/airport",
    },
    { label: "Book a chauffeur in Nice", href: "/en/chauffeur/nice" },
    {
      label: "Private chauffeur on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    { label: "Private car service in Cannes", href: "/en/chauffeur/cannes" },
  ],
  cta: {
    title: "Your private chauffeur in Villeneuve-Loubet",
    text: "Marina Baie des Anges, old village or airport transfer — quick reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service Villeneuve-Loubet — private chauffeur",
    description:
      "Private car service in Villeneuve-Loubet: Nice airport transfer (~15 min), Marina Baie des Anges, the beaches and Escoffier's old village.",
    canonical: "/en/chauffeur/villeneuve-loubet",
  },
};

export default villeneuveLoubetEn;
